import RequireAuth from "@/components/RequireAuth"
import {
  COLLAPSIBLE_HEADER_CONTENT_TOP_PADDING,
  CollapsibleScreenHeader,
} from "@/components/prooftree/CollapsibleScreenHeader"
import { Box } from "@/components/ui/box"
import { Button, ButtonText } from "@/components/ui/button"
import { HStack } from "@/components/ui/hstack"
import { Text } from "@/components/ui/text"
import { VStack } from "@/components/ui/vstack"
import { Colors } from "@/constants/Colors"
import {
  FEEDBACK_REPORT_CATEGORIES,
  submitFeedbackReport,
  type FeedbackReportCategory,
} from "@/services/feedbackReports"
import { MessageSquareWarning, Send } from "lucide-react-native"
import { useRef, useState } from "react"
import { Animated, Platform, TextInput, TouchableOpacity } from "react-native"
import { SafeAreaView } from "react-native-safe-area-context"

const categoryLabels: Record<FeedbackReportCategory, string> = {
  bug: "Bug",
  usability: "Usability",
  feature_request: "Feature",
  other: "Other",
}

export default function ModalScreen() {
  return (
    <RequireAuth>
      <FeedbackModalScreen />
    </RequireAuth>
  )
}

function FeedbackModalScreen() {
  const [category, setCategory] = useState<FeedbackReportCategory>("bug")
  const [message, setMessage] = useState("")
  const [contactContext, setContactContext] = useState("")
  const [status, setStatus] = useState("")
  const [submitting, setSubmitting] = useState(false)
  const scrollY = useRef(new Animated.Value(0)).current

  async function submitReport() {
    setSubmitting(true)
    setStatus("")

    try {
      const report = await submitFeedbackReport({
        category,
        message,
        contactContext,
        metadata: { source: "app-modal" },
      })
      setMessage("")
      setContactContext("")
      setStatus(`Feedback report ${report.id.slice(0, 8)} submitted.`)
    } catch (error) {
      setStatus(error instanceof Error ? error.message : String(error))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <SafeAreaView
      className="flex-1"
      edges={["top"]}
      style={{ backgroundColor: Colors.dark.surface }}
    >
      <CollapsibleScreenHeader
        scrollY={scrollY}
        eyebrow="Feedback"
        title="Send feedback"
        subtitle="Reports are stored in Supabase for this signed-in profile."
        backFallbackHref="/"
      />
      <Animated.ScrollView
        className="flex-1"
        contentContainerStyle={{
          padding: 24,
          paddingTop: COLLAPSIBLE_HEADER_CONTENT_TOP_PADDING,
          paddingBottom: Platform.OS === "web" ? 64 : 48,
        }}
        showsVerticalScrollIndicator={false}
        scrollEventThrottle={16}
        onScroll={Animated.event([{ nativeEvent: { contentOffset: { y: scrollY } } }], {
          useNativeDriver: false,
        })}
      >
        <Box
          className="rounded-3xl border p-5"
          style={{
            backgroundColor: Colors.dark.surfaceSecondary,
            borderColor: `${Colors.dark.primary}44`,
          }}
        >
          <VStack className="gap-5">
            <HStack className="items-center gap-3">
              <Box
                className="h-12 w-12 items-center justify-center rounded-2xl"
                style={{ backgroundColor: `${Colors.dark.primary}24` }}
              >
                <MessageSquareWarning color={Colors.dark.primary} size={22} />
              </Box>
              <VStack className="flex-1">
                <Text className="text-2xl font-bold text-white">Send feedback</Text>
                <Text className="text-sm leading-5" style={{ color: Colors.dark.textSecondary }}>
                  Reports are stored in Supabase for this signed-in profile.
                </Text>
              </VStack>
            </HStack>

            <HStack className="flex-wrap gap-2">
              {FEEDBACK_REPORT_CATEGORIES.map((item) => {
                const selected = category === item
                return (
                  <TouchableOpacity
                    key={item}
                    activeOpacity={0.86}
                    onPress={() => setCategory(item)}
                  >
                    <Box
                      className="rounded-full border px-4 py-2"
                      style={{
                        backgroundColor: selected ? `${Colors.dark.primary}24` : "transparent",
                        borderColor: selected
                          ? `${Colors.dark.primary}88`
                          : "rgba(255,255,255,0.09)",
                      }}
                    >
                      <Text
                        className="text-sm font-bold"
                        style={{
                          color: selected ? Colors.dark.primary : Colors.dark.textSecondary,
                        }}
                      >
                        {categoryLabels[item]}
                      </Text>
                    </Box>
                  </TouchableOpacity>
                )
              })}
            </HStack>

            <VStack className="gap-2">
              <Text className="text-sm font-bold text-white">What happened?</Text>
              <TextInput
                multiline
                value={message}
                onChangeText={setMessage}
                placeholder="Describe the issue or idea"
                placeholderTextColor={Colors.dark.textTertiary}
                className="min-h-36 rounded-2xl border px-4 py-3 text-white"
                style={{
                  backgroundColor: Colors.dark.surface,
                  borderColor: "rgba(255,255,255,0.08)",
                  textAlignVertical: "top",
                }}
              />
            </VStack>

            <VStack className="gap-2">
              <Text className="text-sm font-bold text-white">Contact context</Text>
              <TextInput
                value={contactContext}
                onChangeText={setContactContext}
                placeholder="Optional email, handle, or context for follow-up"
                placeholderTextColor={Colors.dark.textTertiary}
                className="rounded-2xl border px-4 py-3 text-white"
                style={{
                  backgroundColor: Colors.dark.surface,
                  borderColor: "rgba(255,255,255,0.08)",
                }}
              />
            </VStack>

            {status ? (
              <Text className="text-sm font-bold" style={{ color: Colors.dark.proof }}>
                {status}
              </Text>
            ) : null}

            <Button
              size="xl"
              className="rounded-2xl"
              disabled={submitting}
              style={{
                backgroundColor: Colors.dark.primary,
                opacity: submitting ? 0.72 : 1,
              }}
              onPress={submitReport}
            >
              <Send color={Colors.dark.primaryForeground} size={18} />
              <ButtonText className="font-bold" style={{ color: Colors.dark.primaryForeground }}>
                {submitting ? "Sending" : "Send Feedback"}
              </ButtonText>
            </Button>
          </VStack>
        </Box>
      </Animated.ScrollView>
    </SafeAreaView>
  )
}
